'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import React from 'react'
import { Formik } from 'formik'

// walrus
import Box from '@components/kit/box'
import Pressable, { PressableProps } from '@components/kit/pressable'
import ScrollView from '@components/kit/scroll-view'
import Text from '@components/kit/text'
import Modal from '@components/kit/modal'
import Form, {
  StatusMessage,
  EmailField,
  SubmitButton,
  FormRow,
  FormFooter,
  TextField,
  PhoneField
} from '@components/kit/form'

import { ProfileContext } from '../profile'
import MemberInfoPopper from './member-info-popper'
import Button from '@components/kit/button'
import { useUserStore } from '@store/User'
import { ChatContext } from '../../chat.web'
import { HorizontalRule } from '@components/table'

const MemberAddLabelValuelModal = ({
  open,
  onRequestClose,
  labelTitle,
  valueTitle,
  ValueField,
  onSubmit
}) => {
  const onPressReturn = (e, formik) => {
    // @ts-ignore
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault()
      formik?.handleSubmit()
    }
  }
  return (
    <Modal
      open={open}
      onRequestClose={onRequestClose}
      showCloseButton={false}
      width='320px'
      borderRadius='medium'
    >
      <Formik
        initialValues={{ label: '', value: '' }}
        onSubmit={onSubmit}
        validateOnChange={false}
      >
        {formik => (
          <Form flex='1 0 auto'>
            <FormRow>
              <TextField
                label={labelTitle}
                name='label'
                required
                onKeyPress={e => onPressReturn(e, formik)}
              />
            </FormRow>
            <FormRow>
              <ValueField
                label={valueTitle}
                name='value'
                required
                onKeyPress={e => onPressReturn(e, formik)}
              />
            </FormRow>
            <StatusMessage />
            <FormFooter
              flex='1 0 auto'
              justifyContent='flex-start'
              flexDirection='row'
              gap='04'
            >
              <SubmitButton variant='secondary' size='fit'>
                Save
              </SubmitButton>
              <Button variant='tertiary' onPress={onRequestClose} size='fit'>
                Cancel
              </Button>
            </FormFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  )
}

const MemberAddEmailModal = ({ open, onRequestClose, updateEmailsLocally }) => {
  const { userId } = React.useContext(ChatContext)
  const { addUserEmail } = useUserStore.getState()

  const onSubmit = values => {
    const label = (values?.label || '').toLowerCase()
    const value = (values?.value || '').toLowerCase()
    addUserEmail(userId, { label, value }).then(added => {
      if (!added) {
        return
      }
      updateEmailsLocally(label, value)
      onRequestClose()
    })
  }

  return (
    <MemberAddLabelValuelModal
      open={open}
      onRequestClose={onRequestClose}
      labelTitle='E-mail name'
      valueTitle='E-mail address'
      ValueField={EmailField}
      onSubmit={onSubmit}
    />
  )
}

const MemberAddPhoneModal = ({ open, onRequestClose, updatePhonesLocally }) => {
  const { userId } = React.useContext(ChatContext)
  const { addUserPhone } = useUserStore.getState()

  const onSubmit = values => {
    const label = (values?.label || '').toLowerCase()
    const value = (values?.value || '').toLowerCase()
    addUserPhone(userId, { label, value }).then(added => {
      if (!added) {
        return
      }
      updatePhonesLocally(label, value)
      onRequestClose()
    })
  }

  return (
    <MemberAddLabelValuelModal
      open={open}
      onRequestClose={onRequestClose}
      labelTitle='Phone name'
      valueTitle='Phone number'
      ValueField={PhoneField}
      onSubmit={onSubmit}
    />
  )
}

type MenuItemProps = {
  onPress?: () => void
  label: string
  lastItem?: boolean
}

const MenuItem = ({ onPress, label, lastItem }: MenuItemProps) => {
  return (
    <React.Fragment>
      <Pressable onPress={onPress} paddingX='04' paddingY='08'>
        <Text textColor='newPurple' font='display.14.bold'>
          {label}
        </Text>
      </Pressable>
      {!lastItem && <HorizontalRule height='1px' />}
    </React.Fragment>
  )
}

type MemberAddInfoPopoverProps = {
  onAddEmail: () => void
  onAddPhone: () => void
}

const MemberAddInfoPopover = ({
  onAddEmail,
  onAddPhone
}: MemberAddInfoPopoverProps) => {
  const { isAddInfoPopoverOpen, openAddInfoPopover, closePopovers } =
    React.useContext(ProfileContext)
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null)

  const closeAndPress = onPress => {
    closePopovers()
    onPress()
  }

  return (
    <>
      <Button
        ref={setAnchor}
        variant='tertiary'
        size='fit'
        onPress={openAddInfoPopover}
      >
        Add...
      </Button>
      <MemberInfoPopper open={isAddInfoPopoverOpen} anchorEl={anchor}>
        <Box
          flexDirection='column'
          backgroundColor='white'
          borderRadius='large'
          boxShadow='large'
          maxHeight='200px'
          style={{ overflow: 'hidden' }}
        >
          <ScrollView padding='12'>
            <MenuItem
              label='Add e-mail'
              onPress={() => closeAndPress(onAddEmail)}
            />
            <MenuItem
              label='Add phone number'
              onPress={() => closeAndPress(onAddPhone)}
              lastItem
            />
          </ScrollView>
        </Box>
      </MemberInfoPopper>
    </>
  )
}

export default MemberAddInfoPopover

export { MemberAddEmailModal, MemberAddPhoneModal }
